import type { ModuleId } from '@/modules/catalog';
import type { IconName } from '@/ui/Icon';
import type { ViewAccess } from './navigation';
import { newRequestPath } from './useNewRequest';

/** One thing the "New" menu starts. */
export interface NewEntry {
  readonly id: 'job' | 'lead' | 'customer' | 'invoice';
  readonly label: string;
  /** Where it goes. A page that creates in a dialog opens it on `?new=1`. */
  readonly to: string;
  readonly icon: IconName;
  /** Every capability the target asks for: its page first, then the thing it creates. */
  readonly permissions: readonly [string, ...string[]];
  readonly module: ModuleId;
}

/** Everything the menu can start, in the order it is listed. */
export const NEW_ENTRIES: readonly NewEntry[] = [
  {
    id: 'job',
    label: 'Book a job',
    to: '/jobs/new',
    icon: 'jobs',
    permissions: ['JOB_READ', 'JOB_WRITE'],
    module: 'CORE',
  },
  {
    id: 'lead',
    label: 'New lead',
    to: newRequestPath('/leads'),
    icon: 'leads',
    permissions: ['LEAD_INTAKE'],
    module: 'CORE',
  },
  {
    id: 'customer',
    label: 'New customer',
    to: newRequestPath('/customers'),
    icon: 'customers',
    permissions: ['CUSTOMER_READ', 'CUSTOMER_WRITE'],
    module: 'CORE',
  },
  {
    id: 'invoice',
    label: 'New invoice',
    to: newRequestPath('/invoices'),
    icon: 'invoicing',
    permissions: ['INVOICE_READ', 'INVOICE_WRITE'],
    module: 'BILLING',
  },
];

/**
 * What this person may start: the entries whose module this release ships and whose target they
 * hold every capability for. Hiding is a courtesy; the target guards itself.
 *
 * @param access what the person holds and what this installation ships
 * @returns the entries, in menu order
 */
export function offeredNewEntries({ can, enabled }: ViewAccess): readonly NewEntry[] {
  return NEW_ENTRIES.filter(
    (entry) => enabled(entry.module) && entry.permissions.every((permission) => can(permission)),
  );
}
